import { ProductModel } from '~/models/product';
import { ProductVariantModel } from '~/models/productVariant';
import { useGlobalContentStore } from '~/store/globalContent';

const parsePrice = (price: string | undefined) => parseFloat((price ?? '').replace(/,/g, '').replace(/ /g, ''));

export default function useStructuredData() {
  const globalContent = useGlobalContentStore();

  function buildProductUrl(productItem: ProductModel): string {
    return `${globalContent.domainMap?.url}/${globalContent.domainMap?.productBasePath}/${productItem.url}`;
  }

  /**
   * Builds a product JSON-LD entry for use inside an ItemList.
   */
  function buildProductListItem(productItem: ProductModel): Record<string, any> {
    const fullUrl = buildProductUrl(productItem);
    const activeVariant = productItem.variants[0];

    const unitConversionType = globalContent.getMeasurement === 'metric' ? 'UnitConversionTypeMetric' : 'UnitConversionTypeImperial';
    const versionParametricName = unitConversionType === 'UnitConversionTypeMetric' ? 'UnitConversionMetric' : 'UnitConversionImperial';

    const conversionTypeCode = activeVariant?.productParametrics?.find(p => p.code === unitConversionType)?.listValue?.code ?? null;
    const areaPerPackage = activeVariant?.parametrics?.find(p => p.code === versionParametricName)?.value ?? null;
    const unitCode = conversionTypeCode === 'm' ? 'MTR' : 'MTK';
    const packagePrice = parsePrice(activeVariant?.priceInfo.price);

    const mainPriceSpec: Record<string, any> = conversionTypeCode && areaPerPackage
      ? {
        '@type': 'UnitPriceSpecification',
        price: packagePrice,
        priceCurrency: globalContent.getCurrency,
        referenceQuantity: {
          '@type': 'QuantitativeValue',
          value: areaPerPackage,
          unitCode,
          valueReference: {
            '@type': 'QuantitativeValue',
            value: 1,
            unitCode,
          },
        },
      }
      : {
        '@type': 'UnitPriceSpecification',
        price: packagePrice,
        priceCurrency: globalContent.getCurrency,
      };

    return {
      '@type': 'Product',
      url: fullUrl,
      name: productItem.name,
      image: productItem.imageList,
      description: productItem.metaDescription || activeVariant?.variantDescription || '',
      sku: productItem.partNo,
      mpn: productItem.partNo,
      brand: {
        '@type': 'Brand',
        name: productItem.brand?.name,
      },
      offers: {
        '@type': 'Offer',
        url: fullUrl,
        itemCondition: 'https://schema.org/NewCondition',
        availability: activeVariant?.canAddToCart
          ? 'https://schema.org/InStock'
          : 'https://schema.org/OutOfStock',
        priceSpecification: activeVariant?.priceInfo.isOnSale && activeVariant?.priceInfo.priceBeforeDiscount
          ? [
            mainPriceSpec,
            {
              '@type': 'UnitPriceSpecification',
              priceType: 'https://schema.org/StrikethroughPrice',
              price: parsePrice(activeVariant.priceInfo.priceBeforeDiscount),
              priceCurrency: globalContent.getCurrency,
              ...(mainPriceSpec.referenceQuantity && { referenceQuantity: mainPriceSpec.referenceQuantity }),
            },
          ]
          : mainPriceSpec,
      },
    };
  }

  /**
   * Wraps a list of products in an ItemList JSON-LD object.
   * @param source - optional debug label identifying the originating component
   */
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  function buildItemListJsonLd(products: ProductModel[], _source?: string): Record<string, any> {
    return {
      '@context': 'https://schema.org/',
      '@type': 'ItemList',
      // ...(_source && { source: _source }), // for debug: uncomment this to see what component is adding this
      numberOfItems: products.length,
      itemListElement: products.map(buildProductListItem),
    };
  }

  /**
   * Builds a full product JSON-LD object for a product detail page.
   *
   * - offers.price shows the package price (what the customer pays per unit/box)
   * - offers.priceSpecification shows the unit price (e.g. per m²) when available,
   *   so offers.price and priceSpecification.price are always distinct values
   *
   * Description note: metaDescription already uses shortDescription internally as its
   * primary source, so no separate shortDescription fallback is needed here.
   */
  function buildProductPageJsonLd(
    productItem: ProductModel,
    activeVariant: ProductVariantModel,
    options?: {
      pricePerSquareMeter?: number | null;
      areaPerPackage?: number | null;
      conversionType?: string | null;
      categories?: string[];
      source?: string;
    }
  ): Record<string, any> {
    const { pricePerSquareMeter, areaPerPackage, conversionType, categories, source } = options ?? {};
    const fullUrl = buildProductUrl(productItem);

    const description = productItem.metaDescription || activeVariant.variantDescription || '';
    const packagePrice = parsePrice(activeVariant.priceInfo.price);
    const unitCode = conversionType === 'm' ? 'MTR' : 'MTK';

    const offers: Record<string, any> = {
      '@type': 'Offer',
      url: fullUrl,
      itemCondition: 'https://schema.org/NewCondition',
      availability: (!activeVariant.isInStock && activeVariant.hasIncomingDate)
        ? 'https://schema.org/BackOrder'
        : ((!activeVariant.isInStock || activeVariant.isSoldOut) && !activeVariant.isOnDemandProduct)
          ? 'https://schema.org/OutOfStock'
          : 'https://schema.org/InStock',
    };

    const mainPriceSpec: Record<string, any> = pricePerSquareMeter && areaPerPackage
      ? {
        '@type': 'UnitPriceSpecification',
        price: packagePrice,
        priceCurrency: globalContent.getCurrency,
        referenceQuantity: {
          '@type': 'QuantitativeValue',
          value: areaPerPackage,
          unitCode,
          valueReference: {
            '@type': 'QuantitativeValue',
            value: 1,
            unitCode,
          },
        },
      }
      : {
        '@type': 'UnitPriceSpecification',
        price: packagePrice,
        priceCurrency: globalContent.getCurrency,
      };

    const priceBeforeDiscount = activeVariant.priceInfo.priceBeforeDiscount;
    if (activeVariant.priceInfo.isOnSale && priceBeforeDiscount) {
      offers.priceSpecification = [
        mainPriceSpec,
        {
          '@type': 'UnitPriceSpecification',
          priceType: 'https://schema.org/StrikethroughPrice',
          price: parsePrice(priceBeforeDiscount),
          priceCurrency: globalContent.getCurrency,
          ...(mainPriceSpec.referenceQuantity && { referenceQuantity: mainPriceSpec.referenceQuantity }),
        },
      ];
    } else {
      offers.priceSpecification = mainPriceSpec;
    }

    if (!activeVariant.isInStock && activeVariant.hasIncomingDate && (activeVariant.incomingDatePrimary || activeVariant.incomingDate)) {
      offers.availabilityStarts = activeVariant.incomingDatePrimary || activeVariant.incomingDate;
    }

    if (activeVariant._ProductVariantRaw.priceInfo?.priceValidUntil) {
      offers.priceValidUntil = activeVariant._ProductVariantRaw.priceInfo.priceValidUntil?.substring(0, 10);
    }

    const jsonLd: Record<string, any> = {
      '@context': 'https://schema.org/',
      '@type': 'Product',
      ...(source && { source }),
      name: productItem.name,
      image: productItem.imageList,
      description,
      sku: productItem.partNo,
      mpn: productItem.partNo,
      brand: {
        '@type': 'Brand',
        name: productItem.brand?.name,
      },
      offers,
    };

    if (categories?.length) {
      jsonLd.category = categories.join(' > ');
    }

    const productSurfaceColour = productItem.parametricByCode('ProductSurfaceColour');
    if (productSurfaceColour?.value) {
      jsonLd.color = productSurfaceColour.value;
    }

    return jsonLd;
  }

  return {
    buildProductListItem,
    buildItemListJsonLd,
    buildProductPageJsonLd,
  };
}
